import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaCalendarAlt, 
  FaClock, 
  FaUser, 
  FaTag, 
  FaCheckCircle, 
  FaQuoteLeft, 
  FaArrowRight,
  FaSearch,
  FaEnvelope,
  FaInfoCircle
} from "react-icons/fa";
import "../Style/Blog.css";
import { blogPosts } from "./blogData";
import heroVideo from "../assets/make_a_video_this.mp4";

function Blog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  const categories = ["All", "Sustainability", "ISO Standards", "Food Safety", "Cybersecurity", "Resort Operations"];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const handleSubscribe = (e) => {
    e.preventDefault();
    alert(`Subscribed successfully: ${emailInput}`);
    setEmailInput("");
  };

  const resetPagination = () => setCurrentPage(1);

  // ==========================================
  // 1. SINGLE POST VIEW (Dedicated Page Route)
  // ==========================================
  if (id) {
    const post = blogPosts.find((p) => p.id === id);

    if (!post) {
      return (
        <div className="blog-detail-page">
          <div className="blog-detail-container blog-detail-not-found">
            <h2>Article Not Found</h2>
            <p>The strategic insights article you are looking for does not exist or has been relocated.</p>
            <Link to="/blog" className="blog-back-link" style={{ color: "#1c2f6e", marginTop: "1.5rem", display: "inline-flex", alignItems: "center", gap: "0.5rem", textDecoration: "none" }}>
              <FaArrowLeft /> Return to Knowledge Base
            </Link>
          </div>
        </div>
      );
    }

    const relatedPosts = blogPosts
      .filter((p) => p.id !== post.id)
      .slice(0, 3);

    return (
      <div className="blog-detail-page base-fade-in">
        <header className="blog-detail-hero" style={{ backgroundImage: `url(${post.image})` }}>
          <div className="blog-detail-hero-shade"></div>
          <div className="blog-detail-container blog-detail-hero-content">
            <Link to="/blog" className="blog-back-link" style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", color: "#fff", textDecoration: "none", marginBottom: "1rem" }}>
              <FaArrowLeft /> Back to Knowledge Base
            </Link>
            <div>
              <span className="blog-detail-category">{post.category}</span>
            </div>
            <h1>{post.title}</h1>
            <p>{post.excerpt}</p>
            <div className="blog-detail-meta">
              <span><FaCalendarAlt /> {post.date}</span>
              <span><FaUser /> {post.author}</span>
              <span><FaClock /> {post.readTime}</span>
            </div>
          </div>
        </header>

        <div className="blog-detail-container">
          <div className="blog-detail-layout">
            <article className="blog-detail-article">
              <p className="blog-detail-lead">{post.excerpt}</p>

              <section>
                <h2>Overview &amp; Context</h2>
                <div style={{ whiteSpace: "pre-line", lineHeight: "1.8", color: "#4a5568" }}>
                  {post.detailedContent || "Deploying structured standard frameworks across regional or distributed infrastructure requires clear alignment between management targets and operational realities."}
                </div>
              </section>

              {post.insight && (
                <section style={{ marginTop: "2.5rem" }}>
                  <h2>Strategic Insight</h2>
                  <div className="blog-detail-callout">
                    <p style={{ margin: 0, fontWeight: 600, color: "#1c2f6e", lineHeight: "1.6" }}>
                      <FaQuoteLeft style={{ marginRight: "0.5rem", color: "#f5b400" }} />
                      {post.insight}
                    </p>
                  </div>
                </section>
              )}

              {post.actions && post.actions.length > 0 && (
                <section style={{ marginTop: "2.5rem" }}>
                  <h2>Recommended Operational Actions</h2>
                  <p>To implement and verify these measures effectively, consider integrating the following steps into your internal audit cycles:</p>
                  <div className="blog-detail-callout" style={{ marginTop: "1rem" }}>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {post.actions.map((act, index) => (
                        <li key={index} style={{ display: "flex", gap: "0.75rem", marginBottom: "0.75rem", alignItems: "flex-start" }}>
                          <FaCheckCircle style={{ color: "#1c2f6e", marginTop: "4px", flexShrink: 0 }} />
                          <span>{act}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>
              )}
            </article>

            <aside className="blog-detail-sidebar">
              <div className="blog-detail-author-card">
                <span>Author Profile</span>
                <h3>{post.author}</h3>
                <p>Specialized advisory unit focused on robust compliance frameworks and operational optimization.</p>
              </div>

              <div className="blog-detail-sidebar-card">
                <span>Advisory Support</span>
                <h3>Need Implementation Guidance?</h3>
                <p>Speak directly with our senior compliance engineers to map out your organization's roadmap.</p>
                <Link to="/contact" style={{ marginTop: "1rem", display: "inline-flex", alignItems: "center", gap: "0.5rem", textDecoration: "none" }}>
                  Request Consultation <FaArrowRight />
                </Link>
              </div>
            </aside>
          </div>
        </div>

        <section className="blog-detail-related">
          <div className="blog-detail-container">
            <h2>Related Insights</h2>
            <div className="blog-detail-related-grid">
              {relatedPosts.map((related) => (
                <Link 
                  key={related.id} 
                  to={`/blog/${related.id}`} 
                  className="blog-detail-related-card"
                  style={{ textDecoration: "none" }}
                >
                  <img src={related.image} alt={related.title} />
                  <span>{related.category}</span>
                  <h3>{related.title}</h3>
                  <small>Read Article <FaArrowRight /></small>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ==========================================
  // 2. BLOG LISTING VIEW
  // ==========================================
  const filteredPosts = blogPosts.filter((post) => {
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = blogPosts.find((p) => p.featured) || blogPosts[0];
  const regularPosts = filteredPosts.filter((post) => !post.featured || selectedCategory !== "All" || searchQuery !== "");
  const totalPages = Math.max(1, Math.ceil(regularPosts.length / postsPerPage));
  const currentPosts = regularPosts.slice((currentPage - 1) * postsPerPage, currentPage * postsPerPage);

  return (
    <div className="blog-matrix-layout base-fade-in">
      {/* Background Mask */}
      <div className="maldives-topo-bg">
        <div className="ocean-blur-orb orb-1"></div>
        <div className="ocean-blur-orb orb-2"></div>
      </div>

      {/* Hero Header with Video Background */}
      <header className="matrix-header video-header">
        <div className="hero-video-canvas">
          <video autoPlay loop muted playsInline className="background-hero-video">
            <source src={heroVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="video-overlay-gradient"></div>
        </div>
        <div className="matrix-container header-split">
          <div className="header-text-content">
            <span className="matrix-badge">Knowledge Engine Active</span>
            <h1>Maldives ISO Compliance Blog & Insights</h1>
            <p>
              Professional research, advisory analyses, and international standard guidance maps 
              curated explicitly for Maldivian commerce, eco-resort logistics, and regulatory frameworks.
            </p>
          </div>
        </div>
      </header>

      {/* Search & Filter Bar */}
      <section className="matrix-controls-panel">
        <div className="matrix-container control-flex">
          <div className="search-wrapper">
            <input 
              type="text" 
              placeholder="Search insights by keyword, standard, or sector..." 
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                resetPagination();
              }}
            />
            <FaSearch className="search-icon" />
          </div>

          <div className="filter-tab-row">
            {categories.map((cat) => (
              <button 
                key={cat}
                className={`filter-tab-btn ${selectedCategory === cat ? "active" : ""}`}
                onClick={() => {
                  setSelectedCategory(cat);
                  resetPagination();
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post Banner Linking to Detail Page */}
      {selectedCategory === "All" && searchQuery === "" && featuredPost && (
        <section className="matrix-container featured-post-section">
          <Link to={`/blog/${featuredPost.id}`} className="featured-blog-portal" style={{ textDecoration: "none" }}>
            <div className="featured-image-holder">
              <img src={featuredPost.image} alt={featuredPost.title} />
              <div className="featured-image-overlay"></div>
              <span className="featured-card-tag">Featured Analysis</span>
            </div>
            <div className="featured-content-holder">
              <div className="post-meta-row">
                <span className="meta-item"><FaCalendarAlt /> {featuredPost.date}</span>
                <span className="meta-item"><FaClock /> {featuredPost.readTime}</span>
              </div>
              <h2 className="featured-heading-styled">{featuredPost.title}</h2>
              <p className="featured-excerpt-text">{featuredPost.excerpt}</p>
              <div className="featured-footer-row">
                <span className="author-tag"><FaUser /> By {featuredPost.author}</span>
                <span className="action-trigger-text">Read Analysis <FaArrowRight /></span>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* Grid View Section Linking to Detail Pages */}
      <main className="matrix-container grid-view-section-blog">
        <div className="block-title-header-blog">
          <h3>Latest Strategic Intelligence</h3>
        </div>

        <div className="blog-cards-display-grid">
          {currentPosts.length > 0 ? (
            currentPosts.map((item, index) => (
              <Link 
                key={item.id} 
                to={`/blog/${item.id}`}
                className="blog-portal-card"
                style={{ "--card-index": index, textDecoration: "none" }}
              >
                <div className="blog-card-image-wrapper">
                  <img src={item.image} alt={item.title} className="blog-card-primary-bg" />
                  <div className="blog-card-image-overlay"></div>
                  <span className="blog-card-category-tag">{item.category}</span>
                </div>

                <div className="blog-card-content-body">
                  <div>
                    <div className="blog-card-meta-header">
                      <span className="blog-meta-node"><FaCalendarAlt /> {item.date}</span>
                      <span className="blog-meta-node"><FaClock /> {item.readTime}</span>
                    </div>
                    <h4 className="blog-card-title-heading">{item.title}</h4>
                    <p className="blog-card-purpose-desc">{item.excerpt}</p>
                  </div>
                  
                  <div>
                    <div className="blog-card-divider"></div>
                    <div className="blog-card-action-trigger">
                      <span className="blog-trigger-text">Read Article</span>
                      <div className="blog-trigger-arrow-circle">
                        <FaArrowRight />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div className="matrix-empty-state-card" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "4rem 0" }}>
              <FaInfoCircle className="empty-state-icon" style={{ fontSize: "3rem", color: "#64748b", marginBottom: "1rem" }} />
              <h3>No Advisory Documents Located</h3>
              <p>Refine your structural query terms or switch the publication filter categories block.</p>
            </div>
          )}
        </div>

        {regularPosts.length > postsPerPage && (
          <nav className="blog-pagination" aria-label="Blog pagination">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <button
                type="button"
                key={page}
                className={currentPage === page ? "active" : ""}
                onClick={() => setCurrentPage(page)}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </nav>
        )}
      </main>

      {/* Newsletter Dock */}
      <section className="matrix-newsletter-dock">
        <div className="matrix-container newsletter-flex-wrapper">
          <div className="newsletter-text-block">
            <div className="dock-title-row">
              <FaEnvelope className="envelope-icon" />
              <h4>Subscribe to Intelligence Briefings</h4>
            </div>
            <p>Get immediate operational updates, audit circulars, and framework risk analysis worksheets directly inside your inbox.</p>
          </div>
          <form className="newsletter-input-row" onSubmit={handleSubscribe}>
            <input 
              type="email" 
              placeholder="Enter corporate email address..." 
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              required
            />
            <button type="submit" className="newsletter-cta-btn button-image-bg-effect">
              Join Briefing Registry
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

export default Blog;